import * as React from 'react';

import { Loader } from '@travelnest/merlin.loader';
import theme from '@travelnest/merlin.theme';
import { logging } from '../../../logging';
import { BlueButton, HelpFeaturedContainer } from './components';

// https://docs.aws.amazon.com/cognito-user-identity-pools/latest/APIReference/API_RespondToAuthChallenge.html
export const ErrorMessage: React.FC<React.PropsWithChildren & { error: string }> = ({ error, children }) => {
	switch (error) {
		case 'CodeMismatchException':
			return <>
				We couldn't log you in with this link. Check your email to make sure you are using the most recent login link, or
				{children}
			</>;
		case 'ExpiredCodeException':
			return <>
				Your login link is invalid or has expired. <br />
				{children}
			</>;
		default:
			return <>We encountered an error while logging you in, please contact support to continue.</>;

	}
};

export const NewEmailButton: React.FC<{ email: string, loading: boolean, onSubmit: (email: string) => void }> = ({ email, loading, onSubmit }) => {
	return <BlueButton loading={loading} onClick={() => onSubmit(email)}>Send me a new email</BlueButton>;
};

export const decode = (part: string) => decodeURIComponent(part);

export const decodedQueryParams = (search: string) => {
	const params = search.slice(1).split('&');
	return params.reduce((all, part) => {
		const [name, value] = part.split('=');
		return { ...all, [name]: decode(value) };
	}, {} as { [key: string]: string });
};

export interface ILoginConfirmProps {
	search: string;
	loading: boolean;
	onRequestNewEmail: (to: string) => Promise<void>;
	onConfirmLogin: (email: string, session: string, code: string) => Promise<void>;
}

export const LoginConfirm: React.FC<ILoginConfirmProps> = ({ search, loading, onConfirmLogin, onRequestNewEmail }) => {
	const [error, setError] = React.useState<Error | null>(null);

	const { s: encodedSession, e: encodedEmail, c: encodedCode } = decodedQueryParams(search);
	const email = encodedEmail ? atob(encodedEmail) : '';
	const session = encodedSession ? atob(encodedSession) : '';
	const code = encodedCode ? atob(encodedCode) : '';

	React.useEffect(() => {
		if (!session || !email || !code) {
			const paramsError = { name: 'MissingParametersException', message: 'Query params did not have all required values' };
			logging.error(paramsError);
			setError(paramsError as Error);
		} else {
			onConfirmLogin(email, session, code).catch((err) => {
				logging.error(`Error confirming login for ${email}`, err);
				setError(err);
			});
		}
	}, []);

	return <HelpFeaturedContainer>
		<h1>{error ? 'Something went wrong' : 'We\'re logging you in...'}</h1>
		{error ?
			<ErrorMessage error={error.name}>
				<div style={{ marginTop: theme.layout.spacing.large }}>
					<NewEmailButton loading={loading} email={email} onSubmit={onRequestNewEmail} />
				</div>
			</ErrorMessage>
			:
			<>
				Confirming your login
				<div id="loading-state" style={{ height: '30px' }}>
					<Loader />
				</div>
			</>
		}
	</HelpFeaturedContainer>;
};
